import {
  Environment,
  OrbitControls,
  Sky,
  Box,
  Loader,
  TransformControls,
  SpotLight,
} from "@react-three/drei";
import {
  useFrame,
  Canvas,
  useThree,
  // orthographicCamera,
} from "@react-three/fiber";
import {
  EffectComposer,
  DepthOfField,
  Bloom,
  Noise,
  Vignette,
  SSAO,
  Sepia,
  BrightnessContrast,
  HueSaturation,
} from "@react-three/postprocessing";
import { useRef, Suspense, useState, useEffect } from "react";
import { useControls, Leva, folder } from "leva";
import { BlendFunction } from "postprocessing";
import { Woman } from "./models/Woman_07";

import Avatar from "./avatar";
import { Perf } from "r3f-perf";
import { Object3D } from "three";

// softShadows();

function Controls() {
  const controls = useRef();
  const { camera, gl } = useThree();
  useFrame(() => controls.current.update());

  return (
    <OrbitControls
      ref={controls}
      args={[camera, gl.domElement]}
      target={[0, 1.7, 0]}
      makeDefault
      enableDamping
      dampingFactor={0.1}
      rotateSpeed={0.5}
    />
  );
}

function BBox() {
  const boxRef = useRef();

  useFrame(() => {
    boxRef.current.rotation.y += 0.004;
    boxRef.current.rotation.x += 0.004;
    boxRef.current.rotation.z += 0.004;
  });
  return (
    <Box castShadow receiveShadow ref={boxRef} position={[0, 0.5, 0]}>
      <meshStandardMaterial attach="material" color="white" />
    </Box>
  );
}

export default function Scene({ modelPath, currentPolly, play }) {
  const camera = useRef();
  const spotRef = useRef();
  var myUrl = new URL(window.location.href.replace(/#/g, "?"));
  const [preset, setPreset] = useState(myUrl.searchParams.get("preset") || 0);

  const [spotTarget] = useState(() => new Object3D());

  window.tess = () => {
    console.log(222, spotRef.current);
    // spotRef.current.target = spotTarget;
  };

  const lightControls = () => {
    let pp = {
      env: {
        value: preset != "dark" ? "apartment" : "night",
        options: [
          "sunset",
          "warehouse",
          "dawn",
          "night",
          "forest",
          "apartment",
          "studio",
          "city",
          "park",
          "lobby",
        ],
      },
      envEnabled: false,
      shadows: true,
      posz: {
        value: -1,
        min: -5,
        max: 20,
        step: 0.1,
      },
      sky: false,
      sunY: {
        value: -1,
        min: -5,
        max: 20,
        step: 0.1,
      },
      lightColor: "#ffffff",
      pointIntensity: {
        value: 0.12,
        min: 0,
        max: 3,
        step: 0.01,
      },
      ambientIntensity: {
        value: 0.15,
        min: 0,
        max: 3,
        step: 0.01,
      },
      directionIntensity: {
        value: 0.5,
        min: 0,
        max: 10,
        step: 0.01,
      },
      spotIntensity: {
        value: 0.5,
        min: 0,
        max: 10,
        step: 0.1,
      },
      bias: {
        value: -0.0001239,
        min: -0.01,
        max: 0.01,
        step: 0.000001,
      },
      postProcessing: true,
      maxQ: {
        value: window.devicePixelRatio > 1.5 ? window.devicePixelRatio*0.8 : window.devicePixelRatio,
        min: 0,
        max: 7,
        step: 0.1,
      },
    };

    if (preset == "dark") {
      pp.env.value = "night";
      pp.ambientIntensity.value = 0.2;
      pp.pointIntensity.value = 0.12;
    }
    return pp;
  };
  const {
    env,
    envEnabled,
    shadows,
    sky,
    sunY,
    posz,
    pointIntensity,
    ambientIntensity,
    directionIntensity,
    bias,
    postProcessing,
    maxQ,
    lightColor,
    spotIntensity,
  } = useControls("Light & Env", lightControls());

  let PPControls = () => {
    let dd = {
      DOF: folder({
        DOFEnabled: true,
        focusDistance: {
          value: 0,
          min: 0,
          max: .2,
          step: 0.001,
        },
        focalLength: {
          value: 0.005,
          min: 0,
          max: .2,
          step: 0.001,
        },
        bokehScale: {
          value: 4,
          min: 1,
          max: 5,
          step: 0.01,
        },
        height: {
          value: 480,
          options: [240, 360, 480, 720, 1080],
        },
      }),
      Bloom: folder({
        bloomEnabled: true,
        luminanceThreshold: {
          value: 0.13,
          min: 0,
          max: 1,
          step: 0.01,
        },
        luminanceSmoothing: {
          value: 0.56,
          min: 0,
          max: 1,
          step: 0.001,
        },
        bloomHeight: {
          value: 240,
          options: [240, 360, 480, 720, 1080],
        },
        bloomIntensity: {
          value: 1,
          min: 0,
          max: 1,
          step: 0.01,
        },
        bloomRadius: {
          value: 0.31,
          min: 0,
          max: 1,
          step: 0.01,
        },
        bloomOpacity: {
          value: preset == "dark" ? 0.56 : 0.77,
          min: 0,
          max: 5,
          step: 0.01,
        },
      }),
      Noise: folder({
        noiseEnabled: true,
        noiseOpacity: {
          value: preset == "dark" ? 0.06 : 0.05,
          min: 0,
          max: 1,
          step: 0.01,
        },
      }),
      Vignette: folder({
        vignetteEnabled: preset == "dark" ? true : false,
        vignetteEskil: false,
        vignetteOffset: {
          value: 0.02,
          min: -1,
          max: 2,
          step: 0.01,
        },
        vignetteDarkness: {
          value: 0.4,
          min: -1,
          max: 5,
          step: 0.01,
        },
        vignetteOpacity: {
          value: 2.6,
          min: 0,
          max: 5,
          step: 0.01,
        },
      }),
      SSAO: folder({
        ssaoEnabled: preset == "dark" ? true : false,
        ssaoRadius: {
          value: 0.01,
          min: 0,
          max: 0.5,
          step: 0.01,
        },
        ssaoBias: {
          value: 0.03,
          min: 0.0,
          max: 4,
          step: 0.01,
        },
        ssaoFade: {
          value: -0.6,
          min: -1,
          max: 5,
          step: 0.01,
        },
        ssaoLuminanceInfluence: {
          value: 3.05,
          min: -2,
          max: 10,
          step: 0.01,
        },
        ssaoOpacity: {
          value: 1.8,
          min: 0,
          max: 10,
          step: 0.01,
        },
        ssaoIntensity: {
          value: 30,
          min: 0,
          max: 10000,
          step: 1,
        },
        ssaoBlending: {
          value: BlendFunction.MULTIPLY,
          options: [BlendFunction.NORMAL, BlendFunction.MULTIPLY],
        },
      }),
      Sepia: folder({
        sepiaEnabled: false,
        sepiaOpacity: {
          value: 0.08,
          min: 0,
          max: 1,
          step: 0.01,
        },
      }),
      BrighnessContrast: folder({
        brightnessEnabled: preset == "dark" ? true : false,
        brightnessOpacity: {
          value: 0.6,
          min: 0,
          max: 1,
          step: 0.01,
        },
        brightness: {
          value: -0.05,
          min: -1,
          max: 1,
          step: 0.01,
        },
        contrast: {
          value: 0.02,
          min: -1,
          max: 1,
          step: 0.01,
        },
      }),
      HueSaturation: folder({
        hueEnabled: true,
        hue: {
          value: -0.12,
          min: -1,
          max: 1,
          step: 0.01,
        },
        saturation: {
          value: 0.07,
          min: -1,
          max: 1,
          step: 0.01,
        },
        hueOpacity: {
          value: 1,
          min: 0,
          max: 1,
          step: 0.01,
        },
      }),
    };

    // if (preset == "dark") {
    //   dd.Noise.noiseOpacity.value = 0.17;
    // }

    // dd.DOF
    return dd;
  };

  const {
    DOFEnabled,
    focusDistance,
    focalLength,
    bokehScale,
    height,
    bloomEnabled,
    luminanceThreshold,
    luminanceSmoothing,
    bloomHeight,
    bloomIntensity,
    bloomRadius,
    bloomOpacity,
    noiseEnabled,
    noiseOpacity,
    vignetteEnabled,
    vignetteEskil,
    vignetteOffset,
    vignetteDarkness,
    vignetteOpacity,
    ssaoEnabled,
    ssaoOpacity,
    ssaoIntensity,
    ssaoRadius,
    ssaoBias,
    ssaoFade,
    ssaoLuminanceInfluence,
    ssaoBlending,
    sepiaEnabled,
    sepiaOpacity,
    brightnessEnabled,
    brightness,
    contrast,
    brightnessOpacity,
    hueEnabled,
    hue,
    saturation,
    hueOpacity,
  } = useControls("Post Processing", PPControls());

  return (
    <>
      <div className="absolute right-0 w-70 z-50">
        <Leva
          fill // default = false,  true makes the pane fill the parent dom node it's rendered in
          collapsed // default = false, when true the GUI is collpased
        />
      </div>
      <Canvas
        camera={{ fov: 26, near: 0.01, position: [0, 1.7, 0.75] }}
        shadowmap="true"
        shadows={shadows}
        dpr={maxQ}
        background="#000000"
      >
        <Suspense fallback={null}>
          <ambientLight intensity={ambientIntensity} />

          <TransformControls
            mode="translate"
            // ref={spotRef}
            position={[-0.53, 1.82, 0.35]}
          >
            <pointLight
              // position={[10, 10, 10]}
              intensity={pointIntensity}
              castShadow
              shadow-mapSize={[1024, 1024]}
              shadow-bias={bias}
              color={lightColor}
            />
          </TransformControls>

          <TransformControls
            mode="translate"
            ref={spotRef}
            position={[0.57, 1.55, -0.79]}
          >
            <pointLight
              // position={[10, 10, 10]}
              intensity={0.05}
              castShadow
              shadow-mapSize={[1024, 1024]}
              shadow-bias={bias}
              color={lightColor}
            />
          </TransformControls>
          {/* <TransformControls mode="translate">
            <directionalLight
              intensity={directionIntensity}
              castShadow
              // position={[2.5, 8, posz]}
              shadow-mapSize={[1024, 1024]}
              shadow-bias={bias}
              color={lightColor}
            ></directionalLight>
          </TransformControls> */}

          {/* <TransformControls mode="translate" ref={spotRef} position={[-1.29, 2.1, 0.73]}> */}
          <SpotLight
            distance={5}
            angle={0.15}
            attenuation={5}
            castShadow
            shadow-mapSize={[1024, 1024]}
            anglePower={5} // Diffuse-cone anglePower (default: 5)
            target-position={[0, 1.8, 0]}
            opacity={0.01}
            color={lightColor}
            intensity={spotIntensity}
            // ref={spotRef}
            // position={[0, 0, 0]}
            position={[-1.29, 2.1, 0.73]}
            // target={spotTarget}
            // target={[0, 1.8, 0]}
          >
            {/* <primitive object={spotTarget} position={[0, 1.8, 0]} /> */}
          </SpotLight>
          {/* </TransformControls> */}

          {envEnabled && (
            <Environment
              preset={env}
              background={!sky}
              // intensity={envIntensity}
              resolution={256}
            />
          )}
          {sky && <Sky sunPosition={[10, sunY, -10]} />}
          <Controls />
          {modelPath == "models/woman_07.glb" ? (
            <Woman
              modelPath={modelPath}
              currentPolly={currentPolly}
              play={play}
            />
          ) : (
            <Avatar
              modelPath={modelPath}
              currentPolly={currentPolly}
              play={play}
            />
          )}

          {postProcessing && (
            <EffectComposer>
              {DOFEnabled && (
                <DepthOfField
                  focusDistance={focusDistance}
                  focalLength={focalLength}
                  bokehScale={bokehScale}
                  height={height}
                />
              )}
              {bloomEnabled && (
                <Bloom
                  luminanceThreshold={luminanceThreshold}
                  luminanceSmoothing={luminanceSmoothing}
                  height={bloomHeight}
                  intensity={bloomIntensity}
                  radius={bloomRadius}
                  opacity={bloomOpacity}
                />
              )}
              {noiseEnabled && <Noise opacity={noiseOpacity} color={"black"} />}
              {vignetteEnabled && (
                <Vignette
                  eskil={vignetteEskil}
                  offset={vignetteOffset}
                  darkness={vignetteDarkness}
                  opacity={vignetteOpacity}
                />
              )}
              {ssaoEnabled && (
                <SSAO
                  samples={31}
                  radius={ssaoRadius} // 0-10
                  fade={ssaoFade} // 0-1
                  bias={ssaoBias}
                  luminanceInfluence={ssaoLuminanceInfluence} // 0-1
                  intensity={ssaoIntensity}
                  blendFunction={ssaoBlending}
                  opacity={ssaoOpacity}
                />
              )}

              {sepiaEnabled && <Sepia opacity={sepiaOpacity} />}

              {brightnessEnabled && (
                <BrightnessContrast
                  brightness={brightness}
                  contrast={contrast}
                  opacity={brightnessOpacity}
                />
              )}

              {hueEnabled && (
                <HueSaturation
                  hue={hue}
                  saturation={saturation}
                  opacity={hueOpacity}
                />
              )}

              {/* {<LUT />} */}
            </EffectComposer>
          )}
        </Suspense>
        <Perf />
      </Canvas>
      <Loader />
    </>
  );
}
